<template>
  <div class="pricing-page">
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />

    <!-- Hero -->
    <section class="pricing-hero">
      <div class="pricing-hero-inner">
        <div class="pricing-hero-text" data-aos="fade-up">
          <span class="section-eyebrow" data-aos="fade-up">Pricing</span>
          <h1 class="pricing-hero-title" data-aos="fade-up" data-aos-delay="60">Simple, Transparent Pricing</h1>
          <p class="pricing-hero-sub" data-aos="fade-up" data-aos-delay="120">
            No hidden fees. No long-term contracts. Pay only for what you use, and scale as you grow.
          </p>
          <div class="pricing-toggle" data-aos="fade-up" data-aos-delay="180">
            <span :class="{ active: billingCycle === 'monthly' }" @click="billingCycle = 'monthly'">Monthly</span>
            <button class="toggle-switch" :class="{ on: billingCycle === 'annual' }" @click="billingCycle = billingCycle === 'monthly' ? 'annual' : 'monthly'">
              <span class="toggle-knob"></span>
            </button>
            <span :class="{ active: billingCycle === 'annual' }" @click="billingCycle = 'annual'">
              Annual
              <span class="save-badge">Save 20%</span>
            </span>
          </div>
        </div>
        <div class="pricing-hero-visual" data-aos="fade-left">
          <div class="pricing-savings-card">
            <div class="savings-icon-row">
              <div class="savings-icon savings-icon--1"><i class="fa-solid fa-arrow-trend-down"></i></div>
              <div class="savings-icon savings-icon--2"><i class="fa-solid fa-tags"></i></div>
              <div class="savings-icon savings-icon--3"><i class="fa-solid fa-receipt"></i></div>
            </div>
            <h3 class="savings-title">What you save with FULFILLDROP</h3>
            <div class="savings-items">
              <div class="savings-item" v-for="(s, si) in savingsData" :key="si">
                <div class="savings-item-value">{{ s.value }}</div>
                <div class="savings-item-label">{{ s.label }}</div>
              </div>
            </div>
            <div class="savings-compare">
              <div class="savings-compare-row">
                <span class="compare-label">Traditional 3PL</span>
                <div class="compare-bar-bg"><div class="compare-bar-fill compare-bar-fill--red" style="width:92%"></div></div>
                <span class="compare-val compare-val--red">$8.50/order</span>
              </div>
              <div class="savings-compare-row">
                <span class="compare-label">FULFILLDROP</span>
                <div class="compare-bar-bg"><div class="compare-bar-fill compare-bar-fill--green" style="width:48%"></div></div>
                <span class="compare-val compare-val--green">$3.20/order</span>
              </div>
            </div>
            <div class="savings-badge">
              <i class="fa-solid fa-circle-check"></i>
              Avg. 62% cost savings vs. traditional fulfillment
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Cards -->
    <section class="pricing-cards-section">
      <div class="pricing-cards-inner">
        <div class="pricing-grid">
          <!-- Starter -->
          <div class="pricing-card" data-aos="fade-up" data-aos-delay="0">
            <div class="pricing-card-header">
              <h3 class="pricing-card-name">Starter</h3>
              <p class="pricing-card-desc">For new sellers testing the waters.</p>
            </div>
            <div class="pricing-card-price">
              <span class="price-currency">$</span>
              <span class="price-amount">0</span>
              <span class="price-period">/mo</span>
            </div>
            <p class="pricing-card-note">Free up to 20 orders/mo</p>
            <ul class="pricing-features">
              <li><i class="fa-solid fa-check"></i> Up to 20 orders/month</li>
              <li><i class="fa-solid fa-check"></i> 1 warehouse location</li>
              <li><i class="fa-solid fa-check"></i> Basic order tracking</li>
              <li><i class="fa-solid fa-check"></i> Email support</li>
              <li class="disabled"><i class="fa-solid fa-xmark"></i> Branded packaging</li>
              <li class="disabled"><i class="fa-solid fa-xmark"></i> API access</li>
            </ul>
            <button class="pricing-btn pricing-btn--outline">Start Free</button>
          </div>

          <!-- Growth -->
          <div class="pricing-card pricing-card--featured" data-aos="fade-up" data-aos-delay="100">
            <div class="pricing-card-badge">Most Popular</div>
            <div class="pricing-card-header">
              <h3 class="pricing-card-name">Growth</h3>
              <p class="pricing-card-desc">For scaling sellers doing 100–5K orders/mo.</p>
            </div>
            <div class="pricing-card-price">
              <span class="price-currency">$</span>
              <span class="price-amount">{{ billingCycle === 'monthly' ? '199' : '159' }}</span>
              <span class="price-period">/mo</span>
            </div>
            <p class="pricing-card-note">+ $1.5 per additional order</p>
            <ul class="pricing-features">
              <li><i class="fa-solid fa-check"></i> Up to 500 orders/month</li>
              <li><i class="fa-solid fa-check"></i> 3 warehouse locations</li>
              <li><i class="fa-solid fa-check"></i> QC inspection included</li>
              <li><i class="fa-solid fa-check"></i> Priority support</li>
              <li><i class="fa-solid fa-check"></i> Branded packaging</li>
              <li><i class="fa-solid fa-check"></i> API access</li>
            </ul>
            <button class="pricing-btn pricing-btn--primary">Get Started</button>
          </div>

          <!-- Enterprise -->
          <div class="pricing-card" data-aos="fade-up" data-aos-delay="200">
            <div class="pricing-card-header">
              <h3 class="pricing-card-name">Enterprise</h3>
              <p class="pricing-card-desc">For high-volume sellers 10K+ orders/mo.</p>
            </div>
            <div class="pricing-card-price">
              <span class="price-currency">$</span>
              <span class="price-amount">Custom</span>
            </div>
            <p class="pricing-card-note">Tailored to your volume</p>
            <ul class="pricing-features">
              <li><i class="fa-solid fa-check"></i> Unlimited orders</li>
              <li><i class="fa-solid fa-check"></i> Global warehouse network</li>
              <li><i class="fa-solid fa-check"></i> Dedicated account manager</li>
              <li><i class="fa-solid fa-check"></i> Custom SLA &amp; workflows</li>
              <li><i class="fa-solid fa-check"></i> White-glove onboarding</li>
              <li><i class="fa-solid fa-check"></i> Custom integrations</li>
            </ul>
            <button class="pricing-btn pricing-btn--outline">Contact Sales</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Feature Comparison -->
    <section class="compare-section">
      <div class="compare-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Compare Plans</span>
          <h2 class="section-title">Which plan is right for you?</h2>
        </div>
        <div class="compare-table-wrap" data-aos="fade-up" data-aos-delay="100">
          <table class="compare-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Starter</th>
                <th>Growth</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in compareRows" :key="i">
                <td>{{ row.feature }}</td>
                <td><i :class="row.starter ? 'fa-solid fa-check' : 'fa-solid fa-xmark'" :style="{ color: row.starter ? '#10b981' : '#cbd5e1' }"></i></td>
                <td><i :class="row.growth ? 'fa-solid fa-check' : 'fa-solid fa-xmark'" :style="{ color: row.growth ? '#10b981' : '#cbd5e1' }"></i></td>
                <td><i :class="row.enterprise ? 'fa-solid fa-check' : 'fa-solid fa-xmark'" :style="{ color: row.enterprise ? '#10b981' : '#cbd5e1' }"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="pricing-faq">
      <div class="pricing-faq-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">FAQ</span>
          <h2 class="section-title">Pricing Questions?</h2>
        </div>
        <div class="faq-list">
          <div class="faq-item" v-for="(item, i) in faqItems" :key="i" data-aos="fade-up" :data-aos-delay="i * 60">
            <button class="faq-question" :class="{ open: openFaq === i }" @click="openFaq = openFaq === i ? null : i">
              {{ item.q }}
              <i class="fa-solid fa-chevron-down"></i>
            </button>
            <div class="faq-answer" :class="{ open: openFaq === i }">
              <p>{{ item.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="pricing-cta">
      <div class="pricing-cta-inner" data-aos="fade-up">
        <h2>Ready to scale your supply chain?</h2>
        <p>Talk to our team and get a custom quote for your business.</p>
        <div class="pricing-cta-btns">
          <button class="btn-primary" @click="scrollToContact">Get a Quote</button>
          <button class="btn-ghost" @click="scrollToContact">Talk to an Expert</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'

export default {
  name: 'Pricing',
  components: { Navbar, Footer },
  data() {
    return {
      currentLang: 'en',
      billingCycle: 'monthly',
      openFaq: null,
      savingsData: [
        { value: '62%', label: 'Cost Savings' },
        { value: '3.2d', label: 'Avg. Delivery' },
        { value: '$0', label: 'Setup Fee' },
        { value: '24h', label: 'Dispatch Speed' },
      ],
      compareRows: [
        { feature: 'Orders/month', starter: true, growth: true, enterprise: true },
        { feature: 'Warehouse locations', starter: false, growth: true, enterprise: true },
        { feature: 'QC inspection', starter: false, growth: true, enterprise: true },
        { feature: 'Branded packaging', starter: false, growth: true, enterprise: true },
        { feature: 'API access', starter: false, growth: true, enterprise: true },
        { feature: 'Priority support', starter: false, growth: true, enterprise: true },
        { feature: 'Dedicated account manager', starter: false, growth: false, enterprise: true },
        { feature: 'Custom SLA', starter: false, growth: false, enterprise: true },
        { feature: 'White-glove onboarding', starter: false, growth: false, enterprise: true },
      ],
      faqItems: [
        { q: 'Can I switch plans anytime?', a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of the next billing cycle.' },
        { q: 'What counts as an order?', a: 'An order is any shipment we process for you, from pick & pack to delivery. Returns and canceled orders do not count.' },
        { q: 'Are there any setup fees?', a: 'No. There are no setup fees for Starter or Growth plans. Enterprise plans may include onboarding fees depending on customization needs.' },
        { q: 'Do you offer a free trial?', a: 'Yes! The Starter plan is free for up to 20 orders per month. You can also try Growth risk-free for 14 days.' },
        { q: 'What payment methods do you accept?', a: 'We accept credit cards (Visa, Mastercard, Amex), PayPal, and bank transfers for annual Enterprise plans.' },
      ]
    }
  },
  methods: {
    changeLang(lang) {
      this.currentLang = lang
    },
    scrollToContact() {
      const el = document.getElementById('contact')
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.pricing-page {
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* Hero */
.pricing-hero {
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #312e81 100%);
  padding: 100px 2rem 80px;
  color: #fff;
  overflow: hidden;
}
.pricing-hero-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}
.pricing-hero-title {
  font-size: 2.6rem;
  font-weight: 800;
  margin: 12px 0 16px;
  line-height: 1.15;
}
.pricing-hero-sub {
  font-size: 1.05rem;
  opacity: 0.82;
  line-height: 1.65;
  margin-bottom: 28px;
}
.section-eyebrow {
  display: inline-block;
  background: rgba(255,255,255,0.1);
  color: #a5b4fc;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 5px 14px;
  border-radius: 20px;
}

/* Toggle */
.pricing-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  font-weight: 600;
}
.pricing-toggle span {
  opacity: 0.55;
  cursor: pointer;
  transition: opacity 0.2s;
}
.pricing-toggle span.active {
  opacity: 1;
}
.save-badge {
  display: inline-block;
  background: #10b981;
  color: #fff;
  font-size: 0.68rem;
  padding: 1px 7px;
  border-radius: 8px;
  margin-left: 6px;
  vertical-align: middle;
}
.toggle-switch {
  width: 48px;
  height: 26px;
  background: rgba(255,255,255,0.25);
  border: none;
  border-radius: 13px;
  position: relative;
  cursor: pointer;
  transition: background 0.25s;
}
.toggle-switch.on {
  background: #fff;
}
.toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.25s;
}
.toggle-switch.on .toggle-knob {
  transform: translateX(22px);
  background: #28b5c1;
}

/* Hero Visual – Savings Card */
.pricing-hero-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}
.pricing-savings-card {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 28px;
  backdrop-filter: blur(16px);
  width: 100%;
  max-width: 440px;
}
.savings-icon-row {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.savings-icon {
  width: 42px; height: 42px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem;
}
.savings-icon--1 { background: rgba(34,197,94,0.2); color: #86efac; }
.savings-icon--2 { background: rgba(40,181,193,0.2); color: #a5b4fc; }
.savings-icon--3 { background: rgba(249,115,22,0.2); color: #fdba74; }

.savings-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #f1f5f9;
  margin-bottom: 18px;
}
.savings-items {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 22px;
}
.savings-item {
  text-align: center;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 14px 6px;
}
.savings-item-value {
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  margin-bottom: 4px;
}
.savings-item-label {
  font-size: 0.68rem;
  color: #94a3b8;
  line-height: 1.3;
}
.savings-compare {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.savings-compare-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.compare-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  width: 100px;
  flex-shrink: 0;
}
.compare-bar-bg {
  flex: 1;
  height: 8px;
  background: rgba(255,255,255,0.06);
  border-radius: 4px;
  overflow: hidden;
}
.compare-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.8s ease;
}
.compare-bar-fill--red { background: linear-gradient(90deg, #ef4444, #f87171); }
.compare-bar-fill--green { background: linear-gradient(90deg, #22c55e, #4ade80); }
.compare-val {
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  width: 80px;
  text-align: right;
}
.compare-val--red { color: #fca5a5; }
.compare-val--green { color: #86efac; }

.savings-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #86efac;
  background: rgba(34,197,94,0.1);
  border: 1px solid rgba(34,197,94,0.2);
  border-radius: 10px;
  padding: 10px 14px;
}
.savings-badge i {
  font-size: 0.9rem;
}

/* Pricing Cards */
.pricing-cards-section {
  margin-top: -40px;
  padding: 0 2rem 60px;
  position: relative;
  z-index: 1;
}
.pricing-cards-inner {
  max-width: 1100px;
  margin: 0 auto;
}
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.pricing-card {
  background: #fff;
  border-radius: 18px;
  padding: 32px 28px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
  border: 2px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  transition: transform 0.22s, box-shadow 0.22s;
  position: relative;
}
.pricing-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 40px rgba(40,181,193,0.13);
}
.pricing-card--featured {
  border-color: #28b5c1;
  box-shadow: 0 8px 32px rgba(40,181,193,0.18);
  z-index: 2;
}
.pricing-card-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: #28b5c1;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 16px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.pricing-card-header {
  margin-bottom: 20px;
}
.pricing-card-name {
  font-size: 1.2rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 6px;
}
.pricing-card-desc {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}
.pricing-card-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-bottom: 4px;
}
.price-currency {
  font-size: 1.4rem;
  font-weight: 700;
  color: #28b5c1;
}
.price-amount {
  font-size: 3rem;
  font-weight: 900;
  color: #1e293b;
  line-height: 1;
}
.price-period {
  font-size: 0.9rem;
  color: #94a3b8;
  font-weight: 500;
}
.pricing-card-note {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0 0 20px;
}
.pricing-features {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
  flex: 1;
}
.pricing-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 0.88rem;
  color: #334155;
  border-bottom: 1px solid #f8fafc;
}
.pricing-features li.disabled {
  color: #cbd5e1;
}
.pricing-features li i {
  width: 18px;
  text-align: center;
  font-size: 0.78rem;
}
.pricing-features li .fa-check { color: #10b981; }
.pricing-features li.disabled .fa-xmark { color: #e2e8f0; }
.pricing-btn {
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  border: none;
}
.pricing-btn--primary {
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  box-shadow: 0 4px 16px rgba(40,181,193,0.3);
}
.pricing-btn--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(40,181,193,0.4);
}
.pricing-btn--outline {
  background: #fff;
  color: #28b5c1;
  border: 2px solid #e2e8f0;
}
.pricing-btn--outline:hover {
  border-color: #28b5c1;
  background: #f8f7ff;
}

/* Compare Table */
.compare-section {
  padding: 60px 2rem;
  background: #f8fafc;
}
.compare-inner {
  max-width: 900px;
  margin: 0 auto;
}
.section-header {
  text-align: center;
  margin-bottom: 36px;
}
.section-eyebrow {
  background: #e6f7f9;
  color: #28b5c1;
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 5px 14px;
  border-radius: 20px;
  margin-bottom: 12px;
}
.section-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
}
.compare-table-wrap {
  overflow-x: auto;
}
.compare-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}
.compare-table th {
  background: #f8f7ff;
  padding: 14px 18px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #28b5c1;
  text-align: left;
}
.compare-table th:first-child {
  color: #1e293b;
}
.compare-table td {
  padding: 12px 18px;
  font-size: 0.88rem;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
}
.compare-table tr:last-child td {
  border-bottom: none;
}

/* FAQ */
.pricing-faq {
  padding: 60px 2rem;
}
.pricing-faq-inner {
  max-width: 700px;
  margin: 0 auto;
}
.faq-list {
  margin-top: 28px;
}
.faq-item {
  border-bottom: 1px solid #e2e8f0;
}
.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 0;
  background: none;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  text-align: left;
}
.faq-question i {
  font-size: 0.78rem;
  color: #94a3b8;
  transition: transform 0.25s;
}
.faq-question.open i {
  transform: rotate(180deg);
}
.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}
.faq-answer.open {
  max-height: 200px;
}
.faq-answer p {
  padding: 0 0 16px;
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* CTA */
.pricing-cta {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  padding: 60px 2rem;
  text-align: center;
  color: #fff;
}
.pricing-cta-inner h2 {
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 10px;
}
.pricing-cta-inner p {
  font-size: 1rem;
  opacity: 0.8;
  margin: 0 0 28px;
}
.pricing-cta-btns {
  display: flex;
  gap: 14px;
  justify-content: center;
}
.btn-primary {
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  padding: 13px 28px;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40,181,193,0.4);
}
.btn-ghost {
  background: transparent;
  color: #fff;
  padding: 13px 28px;
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.btn-ghost:hover {
  border-color: #fff;
  background: rgba(255,255,255,0.08);
}

/* Responsive */
@media (max-width: 900px) {
  .pricing-grid {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin: 0 auto;
  }
  .pricing-hero-inner {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .pricing-toggle {
    justify-content: center;
  }
  .pricing-hero-title {
    font-size: 2rem;
  }
  .savings-items {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
